import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Drawer,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import TableDiff from "./VehicleBrandTableDiff";
import { useTableActions } from "../Recruit/RecJobs/SubComponents/hooks/useTableActions";

import { vehicleBrandsApi } from "../../Data/axios";
import { getUserInfoProperty } from "../../Data/UserData";
import config from "../../Data/AuthUser";
import StatusAlert from "../../StatusAlertList/StatusAlert";
import { useSelector } from "react-redux";

function VehicleBrand() {
  const userPermissions = useSelector((state) => state?.user?.permissions);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [brandname, setBrandname] = useState("");
  const [vehicleData, setVehicleData] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [loading, setLoading] = useState(false);
  const [queries, setQueries] = useState({
    page: 1,
    per_page: 10,
  });

  // Image selection states
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  const showAlert = useCallback((message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  }, []);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Reset form data when closing
    setBrandname("");
    setSelectedFile(null);
    setImageUrl(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setImageUrl(null);
      alert("Please select an image file.");
    }
  };

  const fetchData = useCallback(
    async (customParams = {}) => {
      try {
        setLoading(true);

        // Use current queries or passed params
        const currentQueries = { ...queries, ...customParams };

        console.log("Fetching Vehicle Brands - API Request:", {
          endpoint: "/getVehicleBrands",
          params: currentQueries,
        });

        let response = await vehicleBrandsApi.getAll(
          currentQueries.page || 1,
          currentQueries.per_page || 10,
          currentQueries.selection,
          currentQueries.searchValue,
          currentQueries.startDate,
          currentQueries.endDate
        );
        console.log("Vehicle Brands API Response:", response);

        // Check if response is an array or has data property
        let results = Array.isArray(response) ? response : response?.data || [];

        if (!Array.isArray(results)) {
          console.error("API didn't return an array:", results);
          setVehicleData([]);
          return;
        }

        // Format data for TableDiff with logo rendering
        const processedData = results.map((item) => ({
          id: item.id || 0,
          logo: item.logo || "",
          brand: item.brand || "",
          // Add a rendered logo for display
          logoDisplay: item.logo ? (
            <img
              src={config.apiImageUrl + item.logo}
              alt="Logo"
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            "No Logo"
          ),
        }));

        console.log("Processed Vehicle Data:", processedData);
        setVehicleData(processedData);
      } catch (error) {
        console.error("Error fetching vehicle brands:", error);
        setVehicleData([]);
        showAlert("Error fetching vehicle brands", "error");
      } finally {
        setLoading(false);
      }
    },
    [queries, showAlert]
  );

  // Use table actions hook
  const defaultTableActions = useTableActions(vehicleBrandsApi, fetchData);

  // Override handleActionClick to customize behavior
  const tableActions = {
    ...defaultTableActions,
    handleActionClick: async (action, row) => {
      if (action === "Delete") {
        if (window.confirm(`Are you sure you want to delete ${row.brand}?`)) {
          try {
            console.log("Deleting Vehicle Brand - API Request:", {
              id: row.id,
              brand: row.brand,
              endpoint: `/deleteVehicleBrandsByID/${row.id}`,
            });

            await vehicleBrandsApi.deletebyId(row.id);
            console.log("Vehicle Brand deleted successfully");
            showAlert("Vehicle brand deleted successfully", "success");
            fetchData();
          } catch (error) {
            console.error("Error deleting vehicle brand:", error);
            showAlert("Error deleting vehicle brand", "error");
          }
        }
      }
      // Don't handle Edit or View actions since we don't want them
    },
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const SaveDate = async (event) => {
    const last_update = new Date().getTime();
    event.preventDefault();

    if (!selectedFile || !brandname.trim()) {
      showAlert("Please fill all fields and select an image.", "warning");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("brand", brandname.trim());
    formData.append("logo", selectedFile);
    formData.append("createby", getUserInfoProperty("empid"));
    formData.append("last_update", last_update.toString());

    // Log the API request
    console.log("Adding Vehicle Brand - API Request:", {
      brand: brandname.trim(),
      logo: selectedFile?.name,
      createby: getUserInfoProperty("empid"),
      last_update: last_update.toString(),
      endpoint: "/addVehicleBrands",
    });

    try {
      const response = await vehicleBrandsApi.add(formData);

      // Log the API response
      console.log("Vehicle Brand API Response:", response);

      if (response === "1601") {
        showAlert("Vehicle brand already exists", "warning");
      } else if (response === "1602") {
        showAlert("Vehicle brand added successfully", "success");
        fetchData();
        setBrandname("");
        setSelectedFile(null);
        setImageUrl(null);
        setDrawerOpen(false);
      } else {
        console.log("Unexpected response:", response);
        showAlert("Unexpected response from server", "warning");
      }
    } catch (error) {
      console.error("Error adding vehicle brand:", error);
      showAlert("Error uploading vehicle brand.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Table headers configuration
  const headers = [
    {
      key: "logoDisplay",
      label: "Logo",
      sortable: false,
    },
    { key: "brand", label: "Brand Name", sortable: true },
    { key: "id", label: "ID", sortable: true },
  ];

  const handlePageChange = (newPage) => {
    setQueries({ ...queries, page: newPage });
  };

  const handlePerPageChange = (newPerPage) => {
    setQueries({ ...queries, per_page: newPerPage, page: 1 });
  };

  return (
    <div>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Add Vehicle Brand Button */}
      <Box
        display="flex"
        justifyContent="flex-start"
        sx={{ marginBottom: "16px" }}
      >
        {userPermissions?.vehical_data_brand_add !== 0 && (
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={handleDrawerOpen}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              marginTop: "10px",
              marginLeft: "16px",
              paddingY: "8px",
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Add Vehicle Brand
          </Button>
        )}
      </Box>

      {/* TableDiff Component */}
      <TableDiff
        headers={headers}
        data={vehicleData}
        rowKey="id"
        defaultSortKey=""
        tableActions={tableActions}
        tableApis={vehicleBrandsApi}
        onTableUpdate={fetchData}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        queries={queries}
        type="vehicle-brands"
        tableClassName="vehicle-brands-table"
        tableStyle={{ fontSize: "16px", fontFamily: "Ubuntu" }}
      />

      {/* Add Vehicle Brand Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "400px", md: "500px" },
            backgroundColor: "#ffffff",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#000000",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ color: "#000000", fontWeight: 600 }}
            >
              Add Vehicle Brand
            </Typography>
            <IconButton
              edge="end"
              onClick={handleDrawerClose}
              aria-label="close"
              sx={{ color: "#424242" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form Content */}
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              overflow: "auto",
              backgroundColor: "#fafafa",
            }}
          >
            <Card elevation={1} sx={{ mb: 3, backgroundColor: "#ffffff" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "#1976d2", fontWeight: 600 }}
                >
                  Brand Information
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Brand Name *
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="e.g. TATA, Mahindra, Ashok Leyland"
                    type="text"
                    value={brandname}
                    onChange={(e) => setBrandname(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "& input": {
                          color: "#000000",
                        },
                        "&:hover fieldset": {
                          borderColor: "#1976d2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#424242",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "#333", fontWeight: 500 }}
                  >
                    Brand Logo *
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      height: "56px",
                      borderStyle: "dashed",
                      borderColor: "#1976d2",
                      backgroundColor: "#ffffff",
                      color: "#424242",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    {selectedFile ? selectedFile.name : "Choose Logo Image"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>

                  {imageUrl && (
                    <Card
                      elevation={1}
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ color: "#424242" }}
                        >
                          Preview:
                        </Typography>
                        <img
                          src={imageUrl}
                          alt="Selected logo"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              display: "flex",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={SaveDate}
              disabled={loading || !brandname.trim() || !selectedFile}
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: "#1976d2",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#1565c0" },
                "&:disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {loading ? "Saving..." : "Save Brand"}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDrawerClose}
              disabled={loading}
              sx={{
                borderColor: "#424242",
                color: "#424242",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
}

export default VehicleBrand;
